/*eslint no-magic-numbers: ["error", { "ignore": [0,1,2] }]*/
import React, { useEffect, useRef, useState } from 'react'
import { useHandleZip } from '../../../hooks/useHandleZip'
import { useNavigate, useParams } from 'react-router-dom'
import { usePagination } from '../../../hooks/usePagination'
import sortDirection from '../../../constants/sortDirection'
import { getSpeciesForDownload, getSpeciesReportsRequest } from '../../../api/reports'
import qs from 'query-string'
import { showErrorToast } from '../../../utils/toast'
import { buildReportsExpandPath } from '../../../routing/constants'
import ExpandedReportsTable from './ExpandedReportsTable'
import { makeSpeciesTabColumns, makeSpeciesTabRow } from './speciesTabTable'
import {
  Dash,
  ReportsPaginationWrapper,
  ReportsTableWrapper,
  ReportTableTitle,
  ReportTableWrapper,
  RightButtons
} from '../style'
import { FormattedMessage } from 'react-intl'
import Button from '../../../components/Button'
import Download from '../../../assets/icons/download-icon'
import Expand from '../../../assets/icons/expand-icon'
import Table from '../../../components/Table'
import ItemsPerPageDropdown from '../../../components/ItemsPerPageDropdown'
import Pagination from '../../../features/Pagination'
import { getUrlForAllFilters } from '../index'

const defaultPaginationState = {
  totalNumber: 0,
  resultsPerPage: 10,
  totalPages: 0,
  currentPage: 1,
  hasNext: true,
  hasPrevious: false
}

const defaultFilters = {
  siteFilters: {
    region: [],
    country: [],
    system: [],
    threats: [],
    assessmentStatus: [],
    kbaStatus: [],
    kbaClassification: [],
    kbaCriterias: [],
    dateOfMostRecentAssessment: [],
    url: ''
  },
  speciesFilters: {
    speciesList: [],
    classList: [],
    selectedSpecieOptions: []
  },
  ecosystemFilters: {
    ecosystemList: [],
    ecosystemRedListCategory: [],
    url: ''
  },
  ecologicalFilters: {
    ecoregionList: [],
    url: ''
  },
  showReports: false
}

const SpeciesReportsTab = ({
  filters,
  setFilters,
  setLoadingScreenMessage,
  isSpeciesLoading,
  setIsSpeciesLoading,
  isRunReportTriggered
}) => {
  const { downloadZip } = useHandleZip()
  const history = useNavigate()
  const params = useParams()
  const { pagination, setPagination, selectPage, nextPage, prevPage, nextSection, prevSection, changeResultsPerPage } =
    usePagination(defaultPaginationState)
  const [speciesReports, setSpeciesReports] = useState([{
    commonName: '',
    iucnStatus: '',
    kbaStatus: '',
    region: [],
    country: [],
    redListCrit: null,
    restrictedRangeStatus: false,
    ecoregionBioregionRestricted: '',
    parameter: '',
    bestGlobalEstimate: null,
    earliestYear: null,
    siteEstimate: '',
    reproductiveUnits: null,
    sourceReproductiveUnits: '',
    SpeciesCriteriaTriggered: []
  }])
  const [speciesSorting, setSpeciesSorting] = useState({
    sortBy: 'specieName',
    sortDirection: sortDirection.ASC
  })
  const prevRunReportTriggered = useRef(isRunReportTriggered)

  const getSpeciesReports = async () => {
    if (isSpeciesLoading) {
      return
    }
    const speciesPagination = {
      resultsPerPage: pagination.resultsPerPage,
      currentPage: pagination.currentPage
    }
    const sorting = {
      sortBy: speciesSorting.sortBy,
      sortDirection: speciesSorting.sortDirection
    }
    setIsSpeciesLoading(true)

    try {
      const response = await getSpeciesReportsRequest({
        queryStrings: getUrlForAllFilters(filters),
        pagination: qs.stringify(speciesPagination),
        sorting: qs.stringify(sorting)
      })

      if (response.error) {
        setSpeciesReports([])
        setIsSpeciesLoading(false)
        setPagination(defaultPaginationState)
        localStorage.setItem('reportsFilters', JSON.stringify(defaultFilters))
        setFilters(defaultFilters)
        return showErrorToast(response.error, 'showBorder')
      }

      setSpeciesReports(response.siteSpecies)
      setPagination(response.pagination)
      setIsSpeciesLoading(false)

    } catch (err) {
      setSpeciesReports([])
      setIsSpeciesLoading(false)
      setPagination(defaultPaginationState)
      localStorage.setItem('reportsFilters', JSON.stringify(defaultFilters))
      setFilters(defaultFilters)
      return showErrorToast('Something went wrong', 'showBorder')
    }
  }

  const expandTable = () => {
    history(buildReportsExpandPath('speciesAtSite'))
  }

  useEffect(() => {
    if (!filters.showReports) {
      return
    }

    const runTriggered = prevRunReportTriggered.current !== isRunReportTriggered
    prevRunReportTriggered.current = isRunReportTriggered

    // Re-running with new filters must start at page 1 (stale page → empty table).
    if (runTriggered && pagination.currentPage !== 1) {
      setPagination((prev) => {
        return { ...prev, currentPage: 1 }
      })
      return
    }

    getSpeciesReports()
  }, [
    pagination.currentPage,
    pagination.resultsPerPage,
    speciesSorting.sortBy,
    speciesSorting.sortDirection,
    isRunReportTriggered
  ])


  const downloadCSV = async () => {
    setIsSpeciesLoading(true)
    setLoadingScreenMessage('buildingReport')
    const queryString = getUrlForAllFilters(filters)

    try {
      const response = await getSpeciesForDownload(queryString)

      if (response.error) {
        showErrorToast('something go wrong')
        setLoadingScreenMessage('runningReport')
        return setIsSpeciesLoading(false)
      }

      setIsSpeciesLoading(false)
      setLoadingScreenMessage('runningReport')
      return downloadZip(JSON.parse(response.join('')).flat(), 'Species_at_Site_report_')

    } catch (err) {
      showErrorToast('something go wrong')
      setLoadingScreenMessage('runningReport')
      setIsSpeciesLoading(false)
    }
  }

  if (params.title && speciesReports.length !== 0) {
    return <ExpandedReportsTable
      title={params.title}
      loading={isSpeciesLoading}
      rows={makeSpeciesTabRow(speciesReports)}
      columns={makeSpeciesTabColumns(speciesSorting, setSpeciesSorting, speciesReports)}
      totalNumber={pagination.totalNumber}
      resultsPerPage={pagination.resultsPerPage}
      changeResultsPerPage={changeResultsPerPage}
      download={downloadCSV}
    />
  }

  return (
    <div>
      <ReportTableWrapper>
        <ReportTableTitle>
          <FormattedMessage id='Species at site' />
          <Dash>{'—'}</Dash>
          <FormattedMessage id='reports' />
        </ReportTableTitle>
        <RightButtons>
          <Button dataTest='download' withIcon={true} height='32px' marginTop='0px' onClick={downloadCSV}>
            <Download />
            <FormattedMessage id='download' />
          </Button>
          <Button dataTest='expand' withIcon={true} height='32px' marginTop='0px' onClick={expandTable}>
            <Expand />
            <FormattedMessage id='expand' />
          </Button>
        </RightButtons>
      </ReportTableWrapper>
      <ReportsTableWrapper activeTab='siteDetails'>
        <Table
          columns={makeSpeciesTabColumns(speciesSorting, setSpeciesSorting, speciesReports)}
          rows={makeSpeciesTabRow(speciesReports)}
          noClearFilter={true}
          noResultMessage='noSpeciesMatchFilteringCriteria'
          dataTest='speciesTab-list'
          isLoading={isSpeciesLoading}
        />
      </ReportsTableWrapper>
      <ReportsPaginationWrapper>
        <ItemsPerPageDropdown
          currentPage={pagination.currentPage}
          resultsPerPage={pagination.resultsPerPage}
          totalNumber={pagination.totalNumber}
          onChange={changeResultsPerPage}
          dataTest='species-items-per-page'
          menuPlacement='top'
          alternativeOptions={true}
        />
        {pagination.totalPages > 1 && (
          <Pagination
            activePages={pagination.currentPage}
            numberOfPages={pagination.totalPages}
            nextPage={nextPage}
            prevPage={prevPage}
            nextSection={nextSection}
            prevSection={prevSection}
            selectPage={selectPage}
            dataTest='page-number'
          />
        )}
      </ReportsPaginationWrapper>
    </div>
  )
}

export default SpeciesReportsTab
